// @ts-nocheck — migrated from JS; reactstrap / react-select / layout prop friction.
import React, { Fragment, useMemo } from "react";
import IntlMessages from "Util/IntlMessages";
import { useIntl } from "react-intl";
import {
  Row,
  Card,
  CardBody,
  CardTitle,
  Badge,
  Button,
  Progress
} from "reactstrap";
import { Colxx, Separator } from "Components/CustomBootstrap";
import BreadcrumbContainer from "Components/BreadcrumbContainer";
import PerfectScrollbar from "react-perfect-scrollbar";
import { PERFECT_SCROLLBAR_MAX_96 } from "Constants/perfectScrollbarLayout";
import ReactTable from "react-table";
import DataTablePagination from "Components/DataTables/pagination";
import Chart from "react-apexcharts";
import { Sparklines, SparklinesLine } from "react-sparklines";
import { useKpiSparklineLineStyle } from "Hooks/useKpiSparklineLineStyle";
import { ReactifyChartColors } from "Util/ReactifyChartColors";
import {
  LOGISTICS_MAP_FRAME,
  LOGISTICS_MAP_LOAD_LABEL,
  LOGISTICS_MAP_STOP_PIN_STYLES,
  LOGISTICS_MAP_STOP_PIN_FALLBACK
} from "Constants/reactifyInlineStyles";
import { addCommas } from "Util/Utils";
import data from "Data/dashboardLogistics.json";

import "react-table/react-table.css";

function LogisticsDashboard() {
  const colors = useMemo(() => ReactifyChartColors(), []);
  const kpiSparkLineStyle = useKpiSparklineLineStyle(colors.ReactifyChartColor1);
  const intl = useIntl();

  const slaOptions = useMemo(() => {
    const c = colors;
    return {
      chart: { toolbar: { show: false }, zoom: { enabled: false } },
      stroke: { width: [3, 2], curve: "smooth", dashArray: [0, 6] },
      colors: [c.ReactifyChartColor1, c.ReactifyChartColor5],
      dataLabels: { enabled: false },
      legend: { position: "top" },
      xaxis: {
        categories: data.slaTrend.days,
        labels: { style: { colors: c.primaryColor } }
      },
      yaxis: {
        min: 92,
        max: 100,
        labels: {
          style: { colors: c.primaryColor },
          formatter: v => `${v}%`
        }
      },
      grid: { borderColor: c.separatorColor, strokeDashArray: 4 },
      tooltip: { theme: "light" }
    };
    }, [colors]);

  const carrierOptions = useMemo(() => {
    const c = colors;
    return {
      chart: { toolbar: { show: false } },
      plotOptions: { bar: { borderRadius: 4, horizontal: true, barHeight: "70%" } },
      colors: [c.ReactifyChartColor2],
      dataLabels: { enabled: true, formatter: v => `${v}%` },
      xaxis: {
        categories: data.carrierScore.map(x => x.carrier),
        labels: { style: { colors: c.primaryColor } }
      },
      yaxis: { labels: { style: { colors: c.primaryColor } } },
      grid: { borderColor: c.separatorColor },
      tooltip: { theme: "light" }
    };
    }, [colors]);

  const warehouseOptions = useMemo(() => {
    const c = colors;
    return {
      chart: { toolbar: { show: false }, stacked: false },
      plotOptions: { bar: { borderRadius: 4, columnWidth: "55%" } },
      colors: [c.ReactifyChartColor1, c.ReactifyChartColor3],
      dataLabels: { enabled: false },
      xaxis: {
        categories: data.warehouseThroughput.wh,
        labels: { style: { colors: c.primaryColor } }
      },
      yaxis: {
        labels: {
          style: { colors: c.primaryColor },
          formatter: v => addCommas(v)
        }
      },
      legend: { position: "top" },
      grid: { borderColor: c.separatorColor },
      tooltip: { theme: "light" }
    };
    }, [colors]);

  const volumeOptions = useMemo(() => {
    const c = colors;
    return {
      chart: { toolbar: { show: false } },
      plotOptions: { bar: { borderRadius: 4, horizontal: true, barHeight: "65%" } },
      colors: [c.ReactifyChartColor4],
      dataLabels: { enabled: true, formatter: v => addCommas(v) },
      xaxis: {
        categories: data.carrierScore.map(x => x.carrier),
        labels: {
          style: { colors: c.primaryColor },
          formatter: v => addCommas(v)
        }
      },
      yaxis: { labels: { style: { colors: c.primaryColor } } },
      grid: { borderColor: c.separatorColor },
      tooltip: { theme: "light" }
    };
    }, [colors]);

  const exceptionsColumns = useMemo(() => {
    const { formatMessage } = intl;
    return [
      {
        Header: formatMessage({ id: "logistics.col.id" }),
        accessor: "id",
        Cell: p => <code className="small">{p.value}</code>
      },
      {
        Header: formatMessage({ id: "logistics.col.type" }),
        accessor: "type"
      },
      {
        Header: formatMessage({ id: "logistics.col.lane" }),
        accessor: "lane"
      },
      {
        Header: formatMessage({ id: "logistics.col.severity" }),
        accessor: "severity",
        Cell: p => {
          const col =
            p.value === "high"
              ? "danger"
              : p.value === "medium"
                ? "warning"
                : "secondary";
          return <Badge color={col}>{p.value}</Badge>;
        }
      },
      {
        Header: formatMessage({ id: "logistics.col.age" }),
        accessor: "ageHrs",
        Cell: p => `${p.value}h`
      }
    ];
    }, [intl]);

  const fleetColumns = useMemo(() => {
    const { formatMessage } = intl;
    return [
      {
        Header: formatMessage({ id: "logistics.col.truck" }),
        accessor: "id",
        Cell: p => <span className="font-weight-medium">{p.value}</span>
      },
      { Header: formatMessage({ id: "logistics.col.driver" }), accessor: "driver" },
      { Header: formatMessage({ id: "logistics.col.origin" }), accessor: "origin" },
      { Header: formatMessage({ id: "logistics.col.dest" }), accessor: "dest" },
      { Header: formatMessage({ id: "logistics.col.eta" }), accessor: "eta" },
      {
        Header: formatMessage({ id: "logistics.col.status" }),
        accessor: "status",
        Cell: p => (
          <Badge color={p.value === "delayed" ? "warning" : "success"}>
            {p.value.replace("_", " ")}
          </Badge>
        )
      }
    ];
    }, [intl]);

  const k = data.kpis;

  return (
      <div className="dashboard-vertical dashboard-vertical--logistics">
        <Fragment>
          <Row>
            <Colxx xxs="12">
              <BreadcrumbContainer
                heading={<IntlMessages id="menu.logistics" />}
              />
              <Separator className="mb-5" />
            </Colxx>
          </Row>

          <Row className="mb-2">
            <Colxx md="8" className="mb-3">
              <Card className="border-primary border-opacity-25 bg-light">
                <CardBody className="py-3 d-flex flex-wrap justify-content-between align-items-center">
                  <div>
                    <h6 className="mb-1">
                      <IntlMessages id="logistics.hero.title" />
                    </h6>
                    <p className="text-muted small mb-0">
                      <IntlMessages id="logistics.hero.sub" />
                    </p>
                  </div>
                  <div className="d-flex gap-2 flex-wrap mt-2 mt-md-0">
                    <Button color="primary" size="sm">
                      <IntlMessages id="logistics.hero.dispatch" />
                    </Button>
                    <Button color="secondary" size="sm" outline>
                      <IntlMessages id="logistics.hero.export" />
                    </Button>
                  </div>
                </CardBody>
              </Card>
            </Colxx>
            <Colxx md="4" className="mb-3">
              <Card className="border-primary border-opacity-25 bg-light h-100">
                <CardBody className="py-3">
                  <p className="text-muted mb-2 small text-uppercase">
                    <IntlMessages id="logistics.section.ops-alerts" />
                  </p>
                  <PerfectScrollbar style={PERFECT_SCROLLBAR_MAX_96}>
                    <ul className="list-unstyled mb-0 small">
                      {data.alerts.map((a, i) => (
                        <li key={i} className="mb-1">
                          <span className="text-muted me-2">{a.t}</span>
                          {a.msg}
                        </li>
                      ))}
                    </ul>
                  </PerfectScrollbar>
                </CardBody>
              </Card>
            </Colxx>
          </Row>

          <Row>
            <Colxx sm="6" md="4" lg="2" className="mb-4">
              <Card className="h-100 dashboard-vertical__kpi-card">
                <CardBody>
                  <Sparklines data={data.routesSpark} margin={6}>
                    <SparklinesLine style={kpiSparkLineStyle} />
                  </Sparklines>
                  <p className="text-muted mb-1 small text-uppercase">
                    <IntlMessages id="logistics.kpi.routes" />
                  </p>
                  <h4 className="mb-0">{addCommas(k.activeRoutes)}</h4>
                  <span className="text-success small">+{k.routesDelta}%</span>
                </CardBody>
              </Card>
            </Colxx>
            <Colxx sm="6" md="4" lg="2" className="mb-4">
              <Card className="h-100 dashboard-vertical__kpi-card">
                <CardBody>
                  <i className="fa fa-check-circle text-success mb-2 d-block" />
                  <p className="text-muted mb-1 small text-uppercase">
                    <IntlMessages id="logistics.kpi.ontime" />
                  </p>
                  <h4 className="mb-0">{k.onTimePct}%</h4>
                  <span className="text-success small">+{k.onTimeDelta}%</span>
                </CardBody>
              </Card>
            </Colxx>
            <Colxx sm="6" md="4" lg="2" className="mb-4">
              <Card className="h-100 dashboard-vertical__kpi-card">
                <CardBody>
                  <i className="fa fa-exclamation-circle text-warning mb-2 d-block" />
                  <p className="text-muted mb-1 small text-uppercase">
                    <IntlMessages id="logistics.kpi.exceptions" />
                  </p>
                  <h4 className="mb-0">{k.exceptionsOpen}</h4>
                </CardBody>
              </Card>
            </Colxx>
            <Colxx sm="6" md="4" lg="2" className="mb-4">
              <Card className="h-100 dashboard-vertical__kpi-card">
                <CardBody>
                  <i className="fa fa-truck text-info mb-2 d-block" />
                  <p className="text-muted mb-1 small text-uppercase">
                    <IntlMessages id="logistics.kpi.transit" />
                  </p>
                  <h4 className="mb-0">{k.avgTransitHrs}h</h4>
                </CardBody>
              </Card>
            </Colxx>
            <Colxx sm="6" md="4" lg="2" className="mb-4">
              <Card className="h-100 dashboard-vertical__kpi-card">
                <CardBody>
                  <i className="fa fa-warehouse text-primary mb-2 d-block" />
                  <p className="text-muted mb-1 small text-uppercase">
                    <IntlMessages id="logistics.kpi.wh-util" />
                  </p>
                  <h4 className="mb-0">{k.warehouseUtilPct}%</h4>
                  <Progress value={k.warehouseUtilPct} className="mt-2" />
                </CardBody>
              </Card>
            </Colxx>
            <Colxx sm="6" md="4" lg="2" className="mb-4">
              <Card className="h-100 dashboard-vertical__kpi-card">
                <CardBody>
                  <i className="fa fa-dollar text-secondary mb-2 d-block" />
                  <p className="text-muted mb-1 small text-uppercase">
                    <IntlMessages id="logistics.kpi.cpp" />
                  </p>
                  <h4 className="mb-0">${k.costPerPkg}</h4>
                  <span className="text-success small">{k.costDelta}%</span>
                </CardBody>
              </Card>
            </Colxx>
          </Row>

          <Row>
            <Colxx lg="5" className="mb-4">
              <Card className="h-100 overflow-hidden">
                <CardBody>
                  <CardTitle>
                    <IntlMessages id="logistics.map.title" />
                  </CardTitle>
                  <p className="text-muted small">
                    <IntlMessages id="logistics.map.caption" />
                  </p>
                  <div
                    className="position-relative rounded border"
                    style={LOGISTICS_MAP_FRAME}
                  >
                    {data.mapStops.map((s, i) => {
                      const pinStyle =
                        LOGISTICS_MAP_STOP_PIN_STYLES[i] ??
                        LOGISTICS_MAP_STOP_PIN_FALLBACK;
                      return (
                        <div
                          key={s.code}
                          className="position-absolute"
                          style={pinStyle}
                        >
                          <Badge
                            color={s.delay ? "warning" : "primary"}
                            className="shadow-sm"
                            pill
                          >
                            {s.code}
                          </Badge>
                          <div
                            className="small text-center mt-1 text-dark"
                            style={LOGISTICS_MAP_LOAD_LABEL}
                          >
                            {addCommas(s.loads)}
                          </div>
                        </div>
                      );
                    })}
                    <div className="position-absolute bottom-0 start-0 end-0 p-2 bg-white bg-opacity-75 small text-muted border-top">
                      <IntlMessages id="logistics.map.legend" />
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Colxx>
            <Colxx lg="7" className="mb-4">
              <Card className="h-100 dashboard-vertical__chart-card">
                <CardBody>
                  <CardTitle>
                    <IntlMessages id="logistics.chart.sla" />
                  </CardTitle>
                  <Chart
                    options={slaOptions}
                    series={[
                      { name: "On-time %", data: data.slaTrend.onTime },
                      { name: "Target", data: data.slaTrend.target }
                    ]}
                    type="line"
                    height={360}
                    width="100%"
                  />
                </CardBody>
              </Card>
            </Colxx>
          </Row>

          <Row>
            <Colxx lg="4" className="mb-4">
              <Card className="h-100 dashboard-vertical__chart-card">
                <CardBody>
                  <CardTitle>
                    <IntlMessages id="logistics.chart.carrier-otd" />
                  </CardTitle>
                  <Chart
                    options={carrierOptions}
                    series={[
                      {
                        name: "OTD",
                        data: data.carrierScore.map(x => x.otd)
                      }
                    ]}
                    type="bar"
                    height={280}
                    width="100%"
                  />
                </CardBody>
              </Card>
            </Colxx>
            <Colxx lg="4" className="mb-4">
              <Card className="h-100 dashboard-vertical__chart-card">
                <CardBody>
                  <CardTitle>
                    <IntlMessages id="logistics.chart.carrier-vol" />
                  </CardTitle>
                  <Chart
                    options={volumeOptions}
                    series={[
                      {
                        name: "Volume",
                        data: data.carrierScore.map(x => x.volume)
                      }
                    ]}
                    type="bar"
                    height={280}
                    width="100%"
                  />
                </CardBody>
              </Card>
            </Colxx>
            <Colxx lg="4" className="mb-4">
              <Card className="h-100 dashboard-vertical__chart-card">
                <CardBody>
                  <CardTitle>
                    <IntlMessages id="logistics.chart.warehouse" />
                  </CardTitle>
                  <Chart
                    options={warehouseOptions}
                    series={[
                      {
                        name: "Inbound",
                        data: data.warehouseThroughput.inbound
                      },
                      {
                        name: "Outbound",
                        data: data.warehouseThroughput.outbound
                      }
                    ]}
                    type="bar"
                    height={280}
                    width="100%"
                  />
                </CardBody>
              </Card>
            </Colxx>
          </Row>

          <Row>
            <Colxx lg="6" className="mb-4">
              <Card>
                <CardBody>
                  <CardTitle>
                    <IntlMessages id="logistics.table.exceptions" />
                  </CardTitle>
                  <ReactTable
                    defaultPageSize={5}
                    data={data.exceptions}
                    columns={exceptionsColumns}
                    minRows={0}
                    PaginationComponent={DataTablePagination}
                  />
                </CardBody>
              </Card>
            </Colxx>
            <Colxx lg="6" className="mb-4">
              <Card>
                <CardBody>
                  <CardTitle>
                    <IntlMessages id="logistics.table.fleet" />
                  </CardTitle>
                  <ReactTable
                    defaultPageSize={5}
                    data={data.fleetSnapshot}
                    columns={fleetColumns}
                    minRows={0}
                    PaginationComponent={DataTablePagination}
                  />
                </CardBody>
              </Card>
            </Colxx>
          </Row>
        </Fragment>
      </div>
    );
}

export default LogisticsDashboard;
